import { useState } from "react";

import {
  Badge,
  Box,
  Button,
  IconButton,
  Paper,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import { keyframes } from "@mui/system";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";

import ExplorerScoutCanvas from "./ExplorerScoutCanvas";
import { getSurfaceOpener } from "./scoutOpeners";

import ScoutMark from "../../atoms/icons/ScoutMark";

const RAIL_TOP = { xs: 104, md: 0 };
const RAIL_HEIGHT = { xs: "70vh", md: "100%" };
const DISMISSED_KEY = "scoutRailDismissedNudges";

const pulse = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  70% { transform: scale(1.9); opacity: 0; }
  100% { opacity: 0; }
`;

const readDismissed = () => {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || "[]"));
  } catch (err) {
    return new Set();
  }
};

const persistDismissed = (ids) => {
  try {
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify([...ids]));
  } catch (err) {
    return;
  }
};

const buildNudgeId = (surface, surfaceContext) =>
  `${surface}:${surfaceContext?.activeTab || "all"}:${surfaceContext?.resolvedState || "none"}`;

const ScoutRail = ({
  surface,
  surfaceContext,
  onClientAction,
  collapsed,
  onToggleCollapse
}) => {
  const { palette } = useTheme();
  const [dismissed, setDismissed] = useState(readDismissed);

  const opener = getSurfaceOpener(surface, surfaceContext);
  const nudgeId = buildNudgeId(surface, surfaceContext);
  const hasNudge = Boolean(opener?.greeting) && !dismissed.has(nudgeId);

  const dismissNudge = () => {
    const next = new Set([...dismissed, nudgeId]);
    setDismissed(next);
    persistDismissed(next);
  };

  if (!collapsed) {
    return (
      <Box
        sx={{
          position: "sticky",
          top: RAIL_TOP,
          alignSelf: "flex-start",
          flexShrink: 0,
          width: { xs: "100%", md: 360 },
          height: RAIL_HEIGHT,
          borderLeft: `1px solid ${palette.backgroundGray.main}`,
          backgroundColor: palette.background.paper,
          borderRadius: "8px",
          overflow: "hidden"
        }}
      >
        <ExplorerScoutCanvas
          surface={surface}
          surfaceContext={surfaceContext}
          onClientAction={onClientAction}
          variant="rail"
          onCollapse={onToggleCollapse}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "sticky",
        top: RAIL_TOP,
        alignSelf: "flex-start",
        flexShrink: 0,
        display: "flex",
        alignItems: "flex-start",
        gap: 1
      }}
    >
      {hasNudge && (
        <Paper
          elevation={3}
          sx={{
            maxWidth: 240,
            p: 1.5,
            borderRadius: "8px",
            border: `1px solid ${palette.secondary.main}`
          }}
        >
          <Box display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <ScoutMark size={18} />
            <Typography variant="caption" color="secondary">
              Scout noticed
            </Typography>
          </Box>
          <Typography variant="body2" color="textPrimary">
            {opener.greeting}
          </Typography>
          <Box display="flex" gap={1} mt={1}>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              sx={{ color: palette.common.white }}
              onClick={onToggleCollapse}
            >
              Open Scout
            </Button>
            <Button
              variant="text"
              size="small"
              color="inherit"
              onClick={dismissNudge}
            >
              Dismiss
            </Button>
          </Box>
        </Paper>
      )}
      <Box
        sx={{
          width: 56,
          height: RAIL_HEIGHT,
          borderLeft: `1px solid ${palette.backgroundGray.main}`,
          backgroundColor: palette.background.paper,
          borderRadius: "8px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          pt: 1.5,
          gap: 1.5
        }}
      >
        <Box sx={{ position: "relative", width: 36, height: 36 }}>
          {hasNudge && (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                borderRadius: "50%",
                backgroundColor: palette.secondary.main,
                animation: `${pulse} 2s ease-out infinite`,
                "@media (prefers-reduced-motion: reduce)": {
                  animation: "none",
                  opacity: 0
                }
              }}
            />
          )}
          <Badge
            color="warning"
            variant="dot"
            invisible={!hasNudge}
            overlap="circular"
          >
            <IconButton
              onClick={onToggleCollapse}
              aria-label="Open Scout"
              sx={{ position: "relative", p: 0.5 }}
            >
              <ScoutMark size={28} />
            </IconButton>
          </Badge>
        </Box>
        <Tooltip title="Open Scout">
          <IconButton onClick={onToggleCollapse} aria-label="Expand Scout">
            <KeyboardDoubleArrowLeftIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default ScoutRail;
